import { useEffect, useState } from 'react'
import moment from 'moment'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  Legend,
  TooltipProps,
} from 'recharts'

import { Card, CardColors, Select, Separator, Text, Loader } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { DEFAULT_YEAR, YEAR_OPTIONS } from '@/constants/businessConstants'
import { fetchCashflow } from '@/actions/reportActions'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

// TODO: Migrate to V2
import { Dimmer } from 'semantic-ui-react'
import { Colors } from '../../../styles/theme'

// Shortens numbers into using k and M
const formatter = Intl.NumberFormat('en', { notation: 'compact' })

const legendFormatter = (value: string, _entry: unknown) => {
  return <span style={{ color: Colors.darkGray }}>{value}</span>
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (!active || !payload) {
    return null
  }

  const expense = payload.find((item) => item.name === 'Expenses')
  const income = payload.find((item) => item.name === 'Income')

  if (!expense || !income) {
    return null
  }

  const profit = income.payload?.total_sum || 0
  return (
    <div className="flex flex-col gap-2 bg-neutral-0 p-4">
      <Text color={TextColors.BRAND}>{label}</Text>
      <Text className="desc">
        <div className="capitalize font-bold">{income.name}: </div>
        <CurrencyFormatLabel value={income.value} />
      </Text>
      <Text className="desc">
        <div className="capitalize font-bold">{expense.name}: </div>
        <CurrencyFormatLabel value={expense.value} />
      </Text>
      <Text className="desc">
        <div className="capitalize font-bold">Profit: </div>
        <CurrencyFormatLabel value={profit} />
      </Text>
    </div>
  )
}

export const CashflowReport = () => {
  const [fetching, setFetching] = useState(true)
  const [data, setData] = useState<
    Array<{
      expenses: number
      income: number
      month_start: string
      total_sum: number
      year_month: string
    }>
  >([])
  const [year, setYear] = useState(DEFAULT_YEAR)

  useEffect(() => {
    const fetch = async () => {
      setFetching(true)
      const data = await fetchCashflow(year)
      if (data) {
        setData(
          data.map(
            ({ expenses, income, month_start, total_sum, year_month }) => ({
              expenses: Math.abs(parseFloat(expenses)),
              income: parseFloat(income),
              month_start,
              total_sum: parseFloat(total_sum || '0'),
              year_month: moment(year_month).format(DATE_FORMATS.MONTH_YEAR),
            })
          )
        )
      }
      setFetching(false)
    }

    fetch()
  }, [year])

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col md:flex-row items-center justify-between gap-3 mb-4">
        {isMobile && (
          <Select
            options={YEAR_OPTIONS}
            onValueChange={(val) => setYear(val || DEFAULT_YEAR)}
            value={year}
          />
        )}
        <Text variant={TypographyStyles.DISPLAY_XXS} className="w-10/12">
          Annual Cashflow Report
        </Text>

        {!isMobile && (
          <div className="w-2/12">
            <Select
              options={YEAR_OPTIONS}
              onValueChange={(val) => setYear(val || DEFAULT_YEAR)}
              value={year}
            />
          </div>
        )}
      </div>
      <Separator className="my-2 mb-4" />

      {fetching ? (
        <Dimmer active inverted>
          <Loader active>Fetching</Loader>
        </Dimmer>
      ) : (
        <ResponsiveContainer width="99%" height={250}>
          <AreaChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={Colors.lightGreen}
                  stopOpacity={0.6}
                />
                <stop
                  offset="95%"
                  stopColor={Colors.lightGreen}
                  stopOpacity={0.6}
                />
              </linearGradient>
              <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={Colors.green} stopOpacity={0.6} />
                <stop offset="55%" stopColor={Colors.green} stopOpacity={0.7} />
              </linearGradient>
            </defs>
            <XAxis dataKey="year_month" />
            <YAxis
              domain={[0, 'auto']}
              type="number"
              allowDecimals={false}
              tickFormatter={(value) => `$${formatter.format(value)}`}
            />
            <Tooltip<number, string>
              content={(props) => <CustomTooltip {...props} />}
            />
            <Legend
              iconType="square"
              align="left"
              formatter={legendFormatter}
            />
            <Area
              type="linear"
              dataKey="income"
              name="Income"
              stroke={Colors.greenBlue}
              fillOpacity={1}
              fill={Colors.greenBlue}
            />
            <Area
              type="linear"
              dataKey="expenses"
              name="Expenses"
              stroke={Colors.yellowOrange}
              fillOpacity={1}
              fill={Colors.yellowOrange}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
